<template>
  <div class="scoring-dashboard">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-target</v-icon>
                Lead Scoring Results
                <span class="page-inline-subtitle">AI-powered lead analysis and temperature tracking</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn
                  color="red"
                  @click="viewHotLeads"
                  class="modern-btn mr-3"
                  elevation="2"
                >
                  <v-icon left>mdi-fire</v-icon>
                  Hot Leads
                </v-btn>
                <v-btn
                  color="info"
                  @click="viewTemperatureChanges"
                  class="modern-btn mr-3"
                  elevation="2"
                >
                  <v-icon left>mdi-thermometer-lines</v-icon>
                  Changes
                </v-btn>
                <v-btn 
                  color="primary" 
                  @click="fetchScoringResults"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Temperature Stats -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3" v-for="stat in temperatureStats" :key="stat.title">
          <v-card 
            class="stats-card"
            elevation="4"
            hover
            @click="filterByTemperature(stat.filter)"
          >
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper" :class="`${stat.color}--bg`">
                <v-icon size="28" color="white">{{ stat.icon }}</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stat.value }}</div>
                <div class="stats-label">{{ stat.title }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Scoring Results Table -->
      <v-row>
        <v-col cols="12">
          <v-card class="scoring-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <!-- Title Section -->
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      Scoring Results
                    </h2>
                    <div class="results-count" v-if="totalResults > 0">
                      <div class="count-display">
                        <span class="count-number">{{ totalResults }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Filters Section -->
                  <div class="filters-section">
                    <v-chip-group v-model="selectedFilter" class="filter-chips">
                      <v-chip
                        v-for="filter in scoreFilters"
                        :key="filter.value"
                        :color="filter.color"
                        :value="filter.value"
                        filter
                        outlined
                        @click="applyFilter(filter.value)"
                      >
                        <v-icon left small>{{ filter.icon }}</v-icon>
                        {{ filter.text }}
                      </v-chip>
                    </v-chip-group>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-data-table
              :headers="headers"
              :items="scoringResults"
              :loading="loading"
              :server-items-length="totalResults"
              :options.sync="options"
              :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
              class="modern-table"
              @update:options="fetchScoringResults"
            >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="serial-number">{{ ((options.page - 1) * options.itemsPerPage) + index + 1 }}</span>
              </template>

              <template v-slot:[`item.lead_info`]="{ item }">
                <div class="lead-info">
                  <div class="lead-avatar">
                    <v-avatar size="32" :color="getLeadColor(item.lead)">
                      <span class="white--text">{{ getInitials(item.lead) }}</span>
                    </v-avatar>
                  </div>
                  <div class="lead-details">
                    <div class="lead-name">{{ item.lead.full_name }}</div>
                    <div class="lead-company">{{ item.lead.company_name }}</div>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.overall_score`]="{ item }">
                <div class="score-breakdown">
                  <v-chip
                    :color="getScoreColor(item.overall_score)"
                    dark
                    class="score-chip"
                  >
                    {{ item.overall_score }}/100
                  </v-chip>
                  <div class="sub-scores">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          x-small
                          color="blue"
                          dark
                          v-bind="attrs"
                          v-on="on"
                        >
                          F: {{ item.firmographic_score }}
                        </v-chip>
                      </template>
                      <span>Firmographic Score: {{ item.firmographic_score }}/100</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          x-small
                          color="green"
                          dark
                          v-bind="attrs"
                          v-on="on"
                        >
                          B: {{ item.behavioral_score }}
                        </v-chip>
                      </template>
                      <span>Behavioral Score: {{ item.behavioral_score }}/100</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          x-small
                          color="purple"
                          dark
                          v-bind="attrs"
                          v-on="on"
                        >
                          I: {{ item.intent_score }}
                        </v-chip>
                      </template>
                      <span>Intent Score: {{ item.intent_score }}/100</span>
                    </v-tooltip>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.temperature`]="{ item }">
                <div class="temperature-display">
                  <v-chip
                    :color="getTemperatureColor(item.temperature)"
                    dark
                    class="temperature-chip"
                  >
                    <v-icon left small>{{ getTemperatureIcon(item.temperature) }}</v-icon>
                    {{ item.temperature.toUpperCase() }}
                  </v-chip>
                  <div v-if="item.temperature_changed" class="temperature-change">
                    <v-icon small color="orange">mdi-arrow-up</v-icon>
                    <span class="change-text">
                      {{ item.previous_temperature ? `from ${item.previous_temperature}` : 'new' }}
                    </span>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.recommendation`]="{ item }">
                <v-chip
                  :color="getRecommendationColor(item.recommendation)"
                  dark
                  small
                >
                  <v-icon left small>{{ getRecommendationIcon(item.recommendation) }}</v-icon>
                  {{ item.recommendation.toUpperCase() }}
                </v-chip>
              </template>

              <template v-slot:[`item.ai_reasoning`]="{ item }">
                <div class="reasoning-preview">
                  <v-tooltip bottom max-width="400px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>mdi-brain</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ item.ai_reasoning || 'No reasoning available' }}</span>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:[`item.scored_at`]="{ item }">
                <div class="scored-time">
                  <div class="date">{{ formatDate(item.scored_at) }}</div>
                  <div class="time">{{ formatTime(item.scored_at) }}</div>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="viewScoringDetails(item)"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="green"
                    @click="viewLead(item.lead)"
                  >
                    <v-icon small>mdi-account</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Scoring Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="800px">
      <v-card v-if="selectedScoring" class="scoring-details-dialog">
        <v-card-title class="dialog-header">
          <span class="headline">AI Scoring Details</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="dialog-content">
          <!-- Lead Info -->
          <div class="lead-summary mb-4">
            <v-row>
              <v-col cols="6">
                <h3>{{ selectedScoring.lead.full_name }}</h3>
                <p class="subtitle">{{ selectedScoring.lead.company_name }}</p>
              </v-col>
              <v-col cols="6" class="text-right">
                <v-chip
                  large
                  :color="getTemperatureColor(selectedScoring.temperature)"
                  dark
                >
                  <v-icon left>{{ getTemperatureIcon(selectedScoring.temperature) }}</v-icon>
                  {{ selectedScoring.temperature.toUpperCase() }} LEAD
                </v-chip>
              </v-col>
            </v-row>
          </div>

          <!-- Score Breakdown -->
          <div class="score-breakdown-detailed mb-4">
            <h4 class="section-title">Score Breakdown</h4>
            <v-row>
              <v-col cols="12" md="3">
                <div class="score-card">
                  <div class="score-value">{{ selectedScoring.overall_score }}/100</div>
                  <div class="score-label">Overall Score</div>
                </div>
              </v-col>
              <v-col cols="12" md="3">
                <div class="score-card">
                  <div class="score-value">{{ selectedScoring.firmographic_score }}/100</div>
                  <div class="score-label">Firmographic</div>
                  <div class="score-weight">30% weight</div>
                </div>
              </v-col>
              <v-col cols="12" md="3">
                <div class="score-card">
                  <div class="score-value">{{ selectedScoring.behavioral_score }}/100</div>
                  <div class="score-label">Behavioral</div>
                  <div class="score-weight">40% weight</div>
                </div>
              </v-col>
              <v-col cols="12" md="3">
                <div class="score-card">
                  <div class="score-value">{{ selectedScoring.intent_score }}/100</div>
                  <div class="score-label">Intent</div>
                  <div class="score-weight">30% weight</div>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- AI Reasoning -->
          <div class="ai-reasoning mb-4">
            <h4 class="section-title">AI Analysis</h4>
            <div class="reasoning-content">
              <v-icon color="primary" class="mr-2">mdi-brain</v-icon>
              {{ selectedScoring.ai_reasoning || 'No detailed reasoning available' }}
            </div>
          </div>

          <!-- Recommendation -->
          <div class="recommendation-section">
            <h4 class="section-title">Recommendation</h4>
            <v-chip
              large
              :color="getRecommendationColor(selectedScoring.recommendation)"
              dark
            >
              <v-icon left>{{ getRecommendationIcon(selectedScoring.recommendation) }}</v-icon>
              {{ selectedScoring.recommendation.toUpperCase() }}
            </v-chip>
            <p class="recommendation-text mt-2">
              {{ getRecommendationText(selectedScoring.recommendation) }}
            </p>
          </div>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="viewLead(selectedScoring.lead)">
            View Lead Details
          </v-btn>
          <v-btn text @click="detailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: "LeadScoring",
  data() {
    return {
      loading: false,
      scoringResults: [],
      totalResults: 0,
      selectedFilter: null,
      detailsDialog: false,
      selectedScoring: null,
      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ['scored_at'],
        sortDesc: [true]
      },
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Lead', value: 'lead_info', sortable: false, width: '250px' },
        { text: 'Score Breakdown', value: 'overall_score', sortable: true, width: '200px' },
        { text: 'Temperature', value: 'temperature', sortable: true, width: '150px' },
        { text: 'Recommendation', value: 'recommendation', sortable: true, width: '130px' },
        { text: 'AI Reasoning', value: 'ai_reasoning', sortable: false, width: '100px' },
        { text: 'Scored At', value: 'scored_at', sortable: true, width: '140px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '100px' },
      ],
      scoreFilters: [
        { text: 'All', value: null, color: 'grey', icon: 'mdi-format-list-bulleted' },
        { text: 'Hot', value: 'hot', color: 'red', icon: 'mdi-fire' },
        { text: 'Warm', value: 'warm', color: 'orange', icon: 'mdi-thermometer-plus' },
        { text: 'Cold', value: 'cold', color: 'blue', icon: 'mdi-snowflake' },
        { text: 'High Score (80+)', value: 'high_score', color: 'green', icon: 'mdi-trending-up' },
        { text: 'Temperature Changed', value: 'temp_changed', color: 'purple', icon: 'mdi-arrow-up' },
      ],
      currentFilter: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000
      }
    };
  },
  computed: {
    temperatureStats() {
      // This would typically come from an API call
      return [
        {
          title: 'Hot Leads',
          value: this.scoringResults.filter(r => r.temperature === 'hot').length,
          icon: 'mdi-fire',
          color: 'red',
          filter: 'hot'
        },
        {
          title: 'Warm Leads',
          value: this.scoringResults.filter(r => r.temperature === 'warm').length,
          icon: 'mdi-thermometer-plus',
          color: 'orange',
          filter: 'warm'
        },
        {
          title: 'Cold Leads',
          value: this.scoringResults.filter(r => r.temperature === 'cold').length,
          icon: 'mdi-snowflake',
          color: 'blue',
          filter: 'cold'
        },
        {
          title: 'Temp Changes',
          value: this.scoringResults.filter(r => r.temperature_changed).length,
          icon: 'mdi-arrow-up',
          color: 'purple',
          filter: 'temp_changed'
        }
      ];
    }
  },
  async mounted() {
    // Check for route parameters
    if (this.$route.query.temperature) {
      this.currentFilter = this.$route.query.temperature;
      this.selectedFilter = this.currentFilter;
    }
    await this.fetchScoringResults();
  },
  methods: {
    async fetchScoringResults() {
      this.loading = true;
      try {
        const params = {
          page: this.options.page,
          per_page: this.options.itemsPerPage,
        };

        if (this.currentFilter) {
          if (this.currentFilter === 'high_score') {
            params.min_score = 80;
          } else if (this.currentFilter === 'temp_changed') {
            params.temperature_changed = true;
          } else {
            params.temperature = this.currentFilter;
          }
        }

        if (this.options.sortBy.length) {
          params.sort_by = this.options.sortBy[0];
          params.sort_desc = this.options.sortDesc[0];
        }

        const response = await axios.get('lead-scoring', { params });
        this.scoringResults = response.data.data.data;
        this.totalResults = response.data.data.total;

      } catch (error) {
        console.error('Error fetching scoring results:', error);
        this.showErrorMessage('Failed to load scoring results. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    applyFilter(filterValue) {
      this.currentFilter = filterValue;
      this.options.page = 1;
      this.fetchScoringResults();
    },

    filterByTemperature(temperature) {
      this.selectedFilter = temperature;
      this.applyFilter(temperature);
    },

    viewHotLeads() {
      this.selectedFilter = 'hot';
      this.applyFilter('hot');
    },

    viewTemperatureChanges() {
      this.selectedFilter = 'temp_changed';
      this.applyFilter('temp_changed');
    },

    viewScoringDetails(scoring) {
      this.selectedScoring = scoring;
      this.detailsDialog = true;
    },

    viewLead(lead) {
      this.$router.push(`/agents/leadranker-ai/leads/${lead.id}`);
    },

    getLeadColor(lead) {
      const colors = ['blue', 'green', 'purple', 'orange', 'red'];
      const index = lead.id % colors.length;
      return colors[index];
    },

    getInitials(lead) {
      return (lead.first_name?.[0] || '') + (lead.last_name?.[0] || '');
    },

    getTemperatureColor(temperature) {
      const colors = { hot: 'red', warm: 'orange', cold: 'blue' };
      return colors[temperature] || 'grey';
    },

    getTemperatureIcon(temperature) {
      const icons = { hot: 'mdi-fire', warm: 'mdi-thermometer-plus', cold: 'mdi-snowflake' };
      return icons[temperature] || 'mdi-thermometer';
    },

    getScoreColor(score) {
      if (score >= 80) return 'red';
      if (score >= 60) return 'orange';
      if (score >= 40) return 'yellow';
      return 'grey';
    },

    getRecommendationColor(recommendation) {
      const colors = { pursue: 'green', nurture: 'orange', disqualify: 'red' };
      return colors[recommendation] || 'grey';
    },

    getRecommendationIcon(recommendation) {
      const icons = { pursue: 'mdi-rocket', nurture: 'mdi-sprout', disqualify: 'mdi-close-circle' };
      return icons[recommendation] || 'mdi-help-circle';
    },

    getRecommendationText(recommendation) {
      const texts = {
        pursue: 'This lead shows strong buying signals and should be contacted immediately.',
        nurture: 'This lead has potential but needs more engagement and relationship building.',
        disqualify: 'This lead shows low conversion potential and should be deprioritized.'
      };
      return texts[recommendation] || 'No recommendation text available.';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    formatTime(date) {
      return new Date(date).toLocaleTimeString();
    },

    showSuccessMessage(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'success',
        timeout: 5000
      };
    },

    showErrorMessage(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'error',
        timeout: 7000
      };
    }
  }
};
</script>

<style scoped>
/* Reuse similar styles from other components for consistency */
.scoring-dashboard {
  min-height: calc(100vh - 64px);
}

.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.page-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  font-size: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  align-items: flex-start;
}

.page-inline-subtitle {
  font-size: 14px;
  color: #64748b;
  font-weight: 400;
  margin-top: 4px;
  -webkit-text-fill-color: #64748b;
}

/* Stats Cards */
.stats-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(102, 126, 234, 0.15);
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stats-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.red--bg { background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%); }
.orange--bg { background: linear-gradient(135deg, #ffa502 0%, #ff7675 100%); }
.purple--bg { background: linear-gradient(135deg, #a55eea 0%, #3742fa 100%); }

/* Score Breakdown */
.score-breakdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.score-chip {
  font-weight: bold;
  font-size: 14px;
}

.sub-scores {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

/* Temperature Display */
.temperature-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.temperature-chip {
  font-weight: bold;
}

.temperature-change {
  display: flex;
  align-items: center;
  gap: 4px;
}

.change-text {
  font-size: 10px;
  color: #64748b;
}

/* Lead Info */
.lead-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lead-details {
  flex: 1;
}

.lead-name {
  font-weight: 600;
  color: #2d3748;
}

.lead-company {
  font-size: 12px;
  color: #64748b;
}

/* Scored Time */
.scored-time {
  display: flex;
  flex-direction: column;
}

.date {
  font-weight: 600;
  color: #2d3748;
  font-size: 12px;
}

.time {
  font-size: 10px;
  color: #64748b;
}

/* Filter Chips */
.filter-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Dialog Styles */
.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.dialog-content {
  padding: 24px;
}

.lead-summary {
  background: rgba(102, 126, 234, 0.05);
  padding: 16px;
  border-radius: 8px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
}

.score-breakdown-detailed {
  background: rgba(102, 126, 234, 0.05);
  padding: 16px;
  border-radius: 8px;
}

.score-card {
  text-align: center;
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.score-value {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
}

.score-label {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.score-weight {
  font-size: 10px;
  color: #a0aec0;
}

.reasoning-content {
  background: white;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  display: flex;
  align-items: flex-start;
}

.recommendation-text {
  color: #64748b;
  font-style: italic;
}

/* Modern elements */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
}

.modern-card-title {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(240, 147, 251, 0.05) 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.serial-number {
  font-weight: 600;
  color: #64748b;
}
</style>